import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import LoadingScreen from '../components/LoadingScreen'
import alert from '../utils/alertService'
import './FaceRegistration.css'

const REQUIRED_CAPTURES = 3 // Number of angles to capture
const CAPTURE_ANGLES = [
  { id: 0, label: 'Front', instruction: 'Look directly at the camera', angle: 'front' },
  { id: 1, label: 'Left', instruction: 'Turn your head slightly to the left', angle: 'left' },
  { id: 2, label: 'Right', instruction: 'Turn your head slightly to the right', angle: 'right' }
]

const FaceRegistration = () => {
  const history = useHistory()
  const location = useLocation()
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [cameraActive, setCameraActive] = useState(false)
  const [capturedImages, setCapturedImages] = useState([]) // Array of captured images
  const [currentCapture, setCurrentCapture] = useState(0) // Current capture index
  const [isValidating, setIsValidating] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [faceRegistered, setFaceRegistered] = useState(false)
  const [captureSuccess, setCaptureSuccess] = useState(null) // null, true, or false for feedback
  const [showConfirmation, setShowConfirmation] = useState(false) // Show confirmation screen after registration
  const [testDetails, setTestDetails] = useState(null) // Store test details for confirmation
  const [encodingsSaved, setEncodingsSaved] = useState(0) // Number of face encodings saved

  // Get return URL from query params (if coming from test page)
  const searchParams = new URLSearchParams(location.search)
  const returnUrl = searchParams.get('returnUrl') || '/student-dashboard'
  const testId = searchParams.get('testId')

  useEffect(() => {
    const token = localStorage.getItem('access_token')
    if (!token) {
      history.push('/login')
      return
    }

    checkFaceRegistration()

    return () => {
      stopCamera()
    }
  }, [history])

  // Start camera only after loading is complete and video element is available
  useEffect(() => {
    if (!loading && videoRef.current && !cameraActive) {
      startCamera()
    }
  }, [loading, cameraActive])

  const checkFaceRegistration = async () => {
    try {
      const token = localStorage.getItem('access_token')
      const response = await fetch('/api/face/check-registration', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setFaceRegistered(data.face_registered)
      }
    } catch (err) {
      console.error('Error checking face registration:', err)
    } finally {
      setLoading(false)
    }
  }

  const startCamera = async () => {
    try {
      setError('') // Clear any previous error
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        }
      })

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        streamRef.current = stream
        setCameraActive(true)
      } else {
        // Video element not ready, stop the stream and try again later
        stream.getTracks().forEach(track => track.stop())
        console.log('Video element not ready, will retry...')
      }
    } catch (err) {
      console.error('Camera error:', err)
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access in your browser settings and refresh the page.')
      } else if (err.name === 'NotFoundError') {
        setError('No camera found. Please connect a camera and refresh the page.')
      } else if (err.name === 'NotReadableError') {
        setError('Camera is in use by another application. Please close other apps using the camera.')
      } else {
        setError('Unable to access camera. Please ensure camera permissions are granted.')
      }
    }
  }

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCameraActive(false)
  }

  const captureImage = async () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    // Set canvas size to match video
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    // Draw current frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Convert canvas to base64
    const imageData = canvas.toDataURL('image/jpeg', 0.9)
    const base64Data = imageData.split(',')[1]

    // Validate the capture
    setIsValidating(true)
    setCaptureSuccess(null)
    setError('')

    try {
      const token = localStorage.getItem('access_token')
      // Get the expected angle for the current capture
      const expectedAngle = CAPTURE_ANGLES[currentCapture]?.angle || 'front'

      const response = await fetch('/api/face/validate-face-capture', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          image_data: base64Data,
          expected_angle: expectedAngle
        })
      })

      const data = await response.json()

      if (response.ok && data.valid) {
        // Valid capture - add to array
        setCapturedImages(prev => [...prev, imageData])
        setCaptureSuccess(true)
        setCurrentCapture(prev => prev + 1)

        // Brief success feedback
        setTimeout(() => {
          setCaptureSuccess(null)
        }, 1000)
      } else {
        // Invalid capture - show error with specific guidance
        setCaptureSuccess(false)

        // Build error message with priority: orientation > position > detection > quality
        let errorMsg = ''
        if (data.error) {
          errorMsg = data.error
        } else if (data.quality_issues?.length > 0) {
          errorMsg = data.quality_issues.join(', ')
        } else {
          errorMsg = 'Invalid capture'
        }

        // Add helpful context based on what failed
        if (!data.orientation_valid && data.face_detected) {
          // Orientation issue - the error message already contains guidance
          setError(errorMsg)
        } else if (!data.face_centered && data.face_detected) {
          setError(`${errorMsg}. Move your face to the center of the frame.`)
        } else if (!data.face_detected) {
          setError(`${errorMsg}. Make sure your face is clearly visible.`)
        } else {
          setError(`Capture failed: ${errorMsg}. Please try again.`)
        }

        setTimeout(() => {
          setCaptureSuccess(null)
          setError('')
        }, 4000) // Longer timeout for orientation messages
      }
    } catch (err) {
      console.error('Validation error:', err)
      // On network error, reject the capture - validation is required
      setCaptureSuccess(false)
      setError('Network error during validation. Please check your connection and try again.')
      setTimeout(() => {
        setCaptureSuccess(null)
        setError('')
      }, 4000)
    } finally {
      setIsValidating(false)
    }
  }

  const removeLastCapture = () => {
    if (capturedImages.length > 0) {
      setCapturedImages(prev => prev.slice(0, -1))
      setCurrentCapture(prev => Math.max(0, prev - 1))
    }
    setError('')
  }

  const resetCaptures = () => {
    setCapturedImages([])
    setCurrentCapture(0)
    setError('')
  }

  const submitFaces = async () => {
    if (capturedImages.length === 0) {
      setError('Please capture at least one image')
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const token = localStorage.getItem('access_token')
      // Extract base64 data from all captured images
      const imagesData = capturedImages.map(img => img.split(',')[1])

      const response = await fetch('/api/face/register-multiple-faces', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ images: imagesData })
      })

      const data = await response.json()

      if (response.ok) {
        setFaceRegistered(true)
        setEncodingsSaved(data.encodings_saved || capturedImages.length)
        stopCamera()

        // If coming from a test, fetch test details and show confirmation
        if (testId) {
          try {
            const testResponse = await fetch(`/api/tests/${testId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            })
            if (testResponse.ok) {
              const testData = await testResponse.json()
              console.log('Test API response:', testData) // Debug
              // API returns {test: {...}}, so extract the test object
              const test = testData.test || testData
              console.log('Extracted test:', test) // Debug
              setTestDetails(test)
            } else {
              console.error('Test API error:', testResponse.status)
            }
          } catch (err) {
            console.error('Failed to fetch test details:', err)
          }
          // Show confirmation screen
          setShowConfirmation(true)
        } else {
          // Not coming from a test, redirect to dashboard
          alert.success(`Face registered successfully! (${data.encodings_saved} angle(s) saved)`, 3000)
          setTimeout(() => {
            history.push(returnUrl)
          }, 1500)
        }
      } else {
        setError(data.error || 'Failed to register face')
      }
    } catch (err) {
      setError('Network error. Please try again.')
      console.error('Face registration error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBeginTest = () => {
    history.push(`/test-taking/${testId}`)
  }

  const handleGoBack = () => {
    history.push('/available-tests')
  }

  const handleSkip = () => {
    if (testId) {
      alert.warning('Face registration is required to take the test', 5000)
    } else {
      history.push(returnUrl)
    }
  }

  if (loading) {
    return <LoadingScreen message="Loading..." />
  }

  // Show confirmation screen after successful face registration (before test)
  if (showConfirmation && testId) {
    return (
      <div className="face-registration-container">
        <div className="face-registration-card confirmation-card">
          <div className="confirmation-header">
            <div className="success-icon">✓</div>
            <h1>Face Registration Complete!</h1>
            <p className="success-message">
              Your face has been registered successfully{encodingsSaved > 0 ? ` with ${encodingsSaved} angle(s)` : ''}.
            </p>
          </div>

          <div className="test-preview">
            <h2>YOU ARE ABOUT TO BEGIN:</h2>
            <div className="test-info-card">
              {testDetails ? (
                <>
                  <h3>{testDetails.title || 'Test'}</h3>
                  {testDetails.description && (
                    <p className="test-description">{testDetails.description}</p>
                  )}
                  <div className="test-meta">
                    <div className="meta-item">
                      <span className="meta-icon">⏱️</span>
                      <span className="meta-label">Duration:</span>
                      <span className="meta-value">{testDetails.duration_minutes || '--'} minutes</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-icon">❓</span>
                      <span className="meta-label">Questions:</span>
                      <span className="meta-value">{testDetails.total_questions || testDetails.question_count || testDetails.questions?.length || '--'}</span>
                    </div>
                    {testDetails.total_points > 0 && (
                      <div className="meta-item">
                        <span className="meta-icon">🎯</span>
                        <span className="meta-label">Total Points:</span>
                        <span className="meta-value">{testDetails.total_points}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="loading-details">Loading test details...</p>
              )}
            </div>
          </div>

          <div className="confirmation-instructions">
            <h3>Before you begin:</h3>
            <ul>
              <li>Make sure you have a stable internet connection</li>
              <li>Find a quiet place with good lighting</li>
              <li>Keep your face visible in the camera throughout the test</li>
              <li>Do not switch tabs or leave the test window</li>
              <li>The test will auto-submit if time runs out or violations exceed the limit</li>
            </ul>
          </div>

          <div className="confirmation-actions">
            <button className="back-button" onClick={handleGoBack}>
              <span className="button-icon">←</span>
              Not Ready Yet
            </button>
            <button className="begin-test-button" onClick={handleBeginTest}>
              <span className="button-icon">▶</span>
              Begin Test
            </button>
          </div>
        </div>
      </div>
    )
  }

  const allCapturesComplete = capturedImages.length >= REQUIRED_CAPTURES
  const currentAngle = CAPTURE_ANGLES[currentCapture] || CAPTURE_ANGLES[CAPTURE_ANGLES.length - 1]

  return (
    <div className="face-registration-container">
      <div className="face-registration-card">
        <div className="face-registration-header">
          <h1>Face Registration</h1>
          <p>
            {faceRegistered
              ? 'Your face is already registered. You can update it by taking new photos from multiple angles.'
              : 'Register your face from multiple angles for secure test verification. This improves accuracy during tests.'}
          </p>
        </div>

        {/* Progress indicator */}
        <div className="capture-progress">
          <div className="progress-label">
            Captures: {capturedImages.length} / {REQUIRED_CAPTURES}
            {capturedImages.length >= REQUIRED_CAPTURES && ' (Complete!)'}
          </div>
          <div className="progress-bar-container">
            <div
              className="progress-bar-fill"
              style={{ width: `${(capturedImages.length / REQUIRED_CAPTURES) * 100}%` }}
            />
          </div>
          <div className="progress-dots">
            {CAPTURE_ANGLES.map((angle, idx) => (
              <div
                key={angle.id}
                className={`progress-dot ${idx < capturedImages.length ? 'completed' : ''} ${idx === currentCapture ? 'current' : ''}`}
                title={angle.label}
              >
                {idx < capturedImages.length ? '✓' : idx + 1}
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="face-registration-error">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="camera-section">
          <div className="camera-preview">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className={`camera-video ${captureSuccess === true ? 'capture-success' : ''} ${captureSuccess === false ? 'capture-failed' : ''}`}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />

            <div className="camera-overlay">
              <div className="face-guide">
                <div className={`face-guide-circle ${isValidating ? 'validating' : ''}`}></div>
                <p className="angle-instruction">
                  {!allCapturesComplete
                    ? `${currentAngle.label}: ${currentAngle.instruction}`
                    : 'All angles captured! Click Register to complete.'}
                </p>
              </div>
            </div>

            {!cameraActive && (
              <div className="camera-loading">
                <p>Starting camera...</p>
              </div>
            )}

            {isValidating && (
              <div className="validating-overlay">
                <div className="validating-spinner"></div>
                <p>Validating capture...</p>
              </div>
            )}
          </div>

          {/* Captured thumbnails */}
          {capturedImages.length > 0 && (
            <div className="captured-thumbnails">
              {capturedImages.map((img, idx) => (
                <div key={idx} className="thumbnail-item">
                  <img src={img} alt={`Capture ${idx + 1}`} />
                  <span className="thumbnail-label">{CAPTURE_ANGLES[idx]?.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="face-registration-actions">
          {!allCapturesComplete ? (
            <div className="capture-actions">
              <button
                className="capture-button"
                onClick={captureImage}
                disabled={!cameraActive || isValidating || isSubmitting}
              >
                <span className="button-icon">📷</span>
                {isValidating ? 'Validating...' : `Capture ${currentAngle.label} View`}
              </button>
              {capturedImages.length > 0 && (
                <button
                  className="undo-button"
                  onClick={removeLastCapture}
                  disabled={isValidating || isSubmitting}
                >
                  <span className="button-icon">↩</span>
                  Undo Last
                </button>
              )}
            </div>
          ) : (
            <div className="captured-actions">
              <button
                className="reset-button"
                onClick={resetCaptures}
                disabled={isSubmitting}
              >
                <span className="button-icon">🔄</span>
                Start Over
              </button>
              <button
                className="submit-button"
                onClick={submitFaces}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="button-spinner"></span>
                    Registering...
                  </>
                ) : (
                  <>
                    <span className="button-icon">✓</span>
                    Register Face ({capturedImages.length} angles)
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        <div className="face-registration-instructions">
          <h3>Tips for a good registration:</h3>
          <ul>
            <li>Capture your face from {REQUIRED_CAPTURES} different angles for better accuracy</li>
            <li>Ensure your face is well-lit and clearly visible</li>
            <li>Follow the angle instructions for each capture</li>
            <li>Remove glasses or hats that may obscure your face</li>
            <li>Make sure no one else is in the frame</li>
          </ul>
        </div>

        <div className="face-registration-footer">
          {!testId && (
            <button className="skip-button" onClick={handleSkip}>
              {faceRegistered ? 'Back to Dashboard' : 'Skip for Now'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default FaceRegistration
